import React from 'react';
import Icon from '../../../components/AppIcon';

const SLACompliancePanel = ({ analytics }) => {
  const slaCompliance = analytics?.slaComplianceRate || 0;
  const firstContactRate = analytics?.firstContactResolutionRate || 0;

  const getComplianceStatus = (rate) => {
    if (rate >= 95) return { label: 'Excellent', color: 'green' };
    if (rate >= 90) return { label: 'Good', color: 'blue' };
    if (rate >= 80) return { label: 'Fair', color: 'yellow' };
    return { label: 'Needs Attention', color: 'red' };
  };

  const slaStatus = getComplianceStatus(slaCompliance);
  const fcrStatus = getComplianceStatus(firstContactRate);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Target" className="w-5 h-5 text-blue-600" />
          SLA Compliance & Performance Monitoring
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* SLA Compliance */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-medium text-gray-700">SLA Compliance Rate</h4>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              slaStatus?.color === 'green' ? 'bg-green-100 text-green-800' :
              slaStatus?.color === 'blue' ? 'bg-blue-100 text-blue-800' :
              slaStatus?.color === 'yellow'? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
            }`}>
              {slaStatus?.label}
            </span>
          </div>

          <div className="text-center mb-4">
            <p className={`text-5xl font-bold ${
              slaStatus?.color === 'green' ? 'text-green-600' :
              slaStatus?.color === 'blue' ? 'text-blue-600' :
              slaStatus?.color === 'yellow'? 'text-yellow-600' : 'text-red-600'
            }`}>
              {slaCompliance?.toFixed(1)}%
            </p>
            <p className="text-sm text-gray-600 mt-2">Tickets within SLA</p>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                slaStatus?.color === 'green' ? 'bg-green-500' :
                slaStatus?.color === 'blue' ? 'bg-blue-500' :
                slaStatus?.color === 'yellow'? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${slaCompliance}%` }}
            />
          </div>

          {slaCompliance < 90 && (
            <div className="mt-4 bg-yellow-50 rounded-lg p-3 flex items-start gap-2">
              <Icon name="AlertTriangle" className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-800">
                SLA compliance is below target. Review escalation procedures.
              </p>
            </div>
          )}
        </div>

        {/* First Contact Resolution */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-medium text-gray-700">First Contact Resolution</h4>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              fcrStatus?.color === 'green' ? 'bg-green-100 text-green-800' :
              fcrStatus?.color === 'blue' ? 'bg-blue-100 text-blue-800' :
              fcrStatus?.color === 'yellow'? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
            }`}>
              {fcrStatus?.label}
            </span>
          </div>

          <div className="text-center mb-4">
            <p className={`text-5xl font-bold ${
              fcrStatus?.color === 'green' ? 'text-green-600' :
              fcrStatus?.color === 'blue' ? 'text-blue-600' :
              fcrStatus?.color === 'yellow'? 'text-yellow-600' : 'text-red-600'
            }`}>
              {firstContactRate?.toFixed(1)}%
            </p>
            <p className="text-sm text-gray-600 mt-2">Resolved on First Contact</p>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                fcrStatus?.color === 'green' ? 'bg-green-500' :
                fcrStatus?.color === 'blue' ? 'bg-blue-500' :
                fcrStatus?.color === 'yellow'? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${firstContactRate}%` }}
            />
          </div>

          {firstContactRate < 70 && (
            <div className="mt-4 bg-blue-50 rounded-lg p-3 flex items-start gap-2">
              <Icon name="Info" className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-800">
                Consider additional agent training to improve first contact resolution.
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
        <div className="text-center">
          <Icon name="Clock" className="w-6 h-6 text-blue-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">
            {analytics?.avgResolutionTimeHours || 0}h
          </p>
          <p className="text-xs text-gray-600">Avg Resolution Time</p>
        </div>
        <div className="text-center">
          <Icon name="Ticket" className="w-6 h-6 text-green-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">
            {analytics?.resolvedTickets || 0}
          </p>
          <p className="text-xs text-gray-600">Resolved Tickets</p>
        </div>
        <div className="text-center">
          <Icon name="AlertCircle" className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">
            {analytics?.inProgressTickets || 0}
          </p>
          <p className="text-xs text-gray-600">In Progress</p>
        </div>
        <div className="text-center">
          <Icon name="Star" className="w-6 h-6 text-purple-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">
            {analytics?.avgSatisfactionRating || 0}/5
          </p>
          <p className="text-xs text-gray-600">Satisfaction</p>
        </div>
      </div>
    </div>
  );
};

export default SLACompliancePanel;
